import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton } from "@mui/material";
import { Add, Edit, Mail, Print } from "@mui/icons-material";

// Custom Components
import TrainingHoursCell from "../../components/Table/TrainingHoursCell";
import Table from "../../components/ReactTable/Table";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";

// Custom hooks
import { useAssignments } from "../../hooks/assignments";
import { courseTypes } from "../../constants/index";
import {
  getDurationString,
  convertToMinutes,
  hoursAndMinutesToString
} from "../../utils/datetime";
import ConfigureAssignmentModal from "./ConfigureAssignmentModal";
import { enqueueSuccessSnackbar } from "../../redux/actions/snackbarActions";
import { useDispatch } from "react-redux";
import Tooltip from "../../components/Tooltip/Tooltip";
import ButtonGroup from "../../components/Buttons/ButtonGroup";

const filterDuration = t => (rows, columnId, filterValue) =>
  filterValue
    ? rows.filter(
        ({ values: { duration } }) =>
          !!duration &&
          getDurationString(duration, t)
            .toLowerCase()
            .includes(filterValue.trim().toLowerCase())
      )
    : rows;

const CourseTypeCell = ({ cell: { value } }) => {
  return (
    <CourseTypeIcon
      type={
        value === courseTypes.online || value === courseTypes.policy
          ? value
          : courseTypes.ilt
      }
    />
  );
};

const DurationCell = ({ cell: { value } }) => {
  return (
    <TrainingHoursCell
      duration={value}
      data-qa="location-assignments-duration-cell"
    />
  );
};

const columns = t => [
  {
    Header: t("table.columnHeader.type"),
    accessor: "trainingObject.courseType",
    Cell: CourseTypeCell,
    width: 24
  },
  {
    Header: t("table.columnHeader.title"),
    accessor: ({ trainingObject }) => trainingObject.name?.trim(),
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.titleAll"),
    width: 150
  },
  {
    Header: t("table.columnHeader.duration"),
    accessor: ({ trainingObject: { durationHours, durationMinutes } }) =>
      convertToMinutes(durationHours, durationMinutes),
    Cell: DurationCell,
    Filter: TextFilter,
    filter: filterDuration(t),
    filterPlaceholder: t("table.filterPlaceholder.durationAll"),
    id: "duration",
    width: 75
  },
  {
    Header: t("table.columnHeader.assigned"),
    accessor: "assignedCount",
    width: 50
  },
  {
    Header: t("table.columnHeader.inProgress"),
    accessor: "inProgressCount",
    width: 50
  },
  {
    Header: t("table.columnHeader.completed"),
    accessor: "completedCount",
    width: 50
  },
  {
    Cell: () => (
      <Box className="hover-action">
        <Box my={-1}>
          <Tooltip
            title={t("administration.editAssignment")}
            placement="bottom"
          >
            <IconButton color="primary" size="large">
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    ),
    disableSortBy: true,
    id: "action",
    maxWidth: "5%",
    width: "5%"
  }
];

const AssignmentsTable = ({
  referenceType,
  referenceId,
  onAddAssignmentClick,
  onOpenPrintReport,
  onOpenMailReport
}) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const dispatch = useDispatch();
  const {
    fetchAssignments,
    updateAssignment,
    deleteAssignment,
    loading,
    isDeleting
  } = useAssignments();

  const getAssignments = () => {
    fetchAssignments({ referenceId, referenceType }).then(setAssignments);
  };

  useEffect(() => {
    getAssignments();
  }, []);

  const memoColumns = useMemo(() => columns(t), []);

  const handleUpdateAssignment = newAssignmentData => {
    return updateAssignment(newAssignmentData).then(() => {
      getAssignments();
      setSelectedAssignment(null);
      dispatch(
        enqueueSuccessSnackbar({
          message: t("administration.updateAssignmentSuccess")
        })
      );
    });
  };

  const handleCancelAssignment = () => {
    return deleteAssignment(selectedAssignment.id).then(() => {
      getAssignments();
      setSelectedAssignment(null);
      dispatch(
        enqueueSuccessSnackbar({
          message: t("administration.cancelAssignmentSuccess")
        })
      );
    });
  };

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Button
          color="primary"
          variant="contained"
          onClick={onAddAssignmentClick}
          startIcon={<Add />}
        >
          {t("administration.addAssignment")}
        </Button>
        <ButtonGroup>
          <Button
            color="primary"
            onClick={onOpenPrintReport}
            startIcon={<Print />}
            disabled={!referenceId}
          >
            {t("administration.printAssignments")}
          </Button>
          <Button
            color="primary"
            onClick={onOpenMailReport}
            startIcon={<Mail />}
            disabled={!referenceId}
          >
            {t("administration.emailAssignments")}
          </Button>
        </ButtonGroup>
      </Box>
      <Table
        columns={memoColumns}
        data={assignments || []}
        loading={loading}
        manualFilters={false}
        manualSortBy={false}
        cellSize="medium"
        onRowClick={({ original }) => setSelectedAssignment(original)}
      />
      <ConfigureAssignmentModal
        open={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        assignmentId={selectedAssignment?.id}
        daysToComplete={
          selectedAssignment ? selectedAssignment.daysToComplete : 0
        }
        repeatInterval={
          selectedAssignment ? selectedAssignment.repeatInterval : 0
        }
        trainingObjectId={selectedAssignment?.trainingObject?.id}
        referenceId={referenceId}
        referenceType={referenceType}
        onSaveAssignment={handleUpdateAssignment}
        onCancelAssignment={handleCancelAssignment}
        courseName={selectedAssignment?.trainingObject?.name}
        passingScore={
          Number.isInteger(selectedAssignment?.passingScore)
            ? `${selectedAssignment?.passingScore}%`
            : t("passFail")
        }
        duration={hoursAndMinutesToString(
          selectedAssignment?.trainingObject?.durationHours,
          selectedAssignment?.trainingObject?.durationMinutes,
          t
        )}
        isExistingAssignment
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default AssignmentsTable;
